import { deepClone } from '../utils';

type ExportSnapshotProvider = () => any;
type OverlayFactory = (options?: any) => { overlay: HTMLElement; panel: HTMLElement; close: () => void };

type SessionExportOptions = {
  getCurrentSessionId: () => string | null;
  getCurrentSnapshot: ExportSnapshotProvider;
  appendError: (message: string) => void;
  showToast: (message: string) => void;
  createOverlayPanel: OverlayFactory;
};

function sanitizeExportFilenamePart(value: any, fallback = 'session') {
  const cleaned = String(value || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
  return cleaned || fallback;
}

function formatExportTimestamp(value: any) {
  const date = value ? new Date(value) : new Date();
  const safe = Number.isNaN(date.getTime()) ? new Date() : date;
  const pad = (num: number) => String(num).padStart(2, '0');
  return [
    safe.getFullYear(),
    pad(safe.getMonth() + 1),
    pad(safe.getDate()),
  ].join('') + '-' + [
    pad(safe.getHours()),
    pad(safe.getMinutes()),
  ].join('');
}

function buildExportFilename(snapshot: any, ext: string) {
  const timestamp = formatExportTimestamp(snapshot?.updated || new Date().toISOString());
  const title = sanitizeExportFilenamePart(snapshot?.title || '新会话');
  const agent = sanitizeExportFilenamePart(snapshot?.agent || 'session');
  return `${timestamp}-${title}-${agent}.${ext}`;
}

function formatExportValue(value: any) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function serializeMessageContent(content: any) {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return formatExportValue(content);
  return content.map((segment) => {
    if (segment?.type === 'text') return segment.text || '';
    if (segment?.type === 'tool_call') {
      return [
        `[tool:${segment.toolName || 'unknown'}]`,
        segment.input ? `input: ${formatExportValue(segment.input)}` : '',
        segment.output ? `output: ${formatExportValue(segment.output)}` : '',
      ].filter(Boolean).join('\n');
    }
    return formatExportValue(segment);
  }).join('\n\n');
}

function stripToolDataFromMessage(message: any) {
  if (!message || typeof message !== 'object') return message;
  const next = deepClone(message);
  delete next.toolCalls;
  if (Array.isArray(next.segments)) {
    next.segments = next.segments.filter((segment) => segment?.type !== 'tool_call');
  }
  if (Array.isArray(next.content)) {
    next.content = next.content.filter((segment) => segment?.type !== 'tool_call');
  }
  const serialized = serializeMessageContent(next.content || '');
  const hasAttachments = Array.isArray(next.attachments) && next.attachments.length > 0;
  if (!String(serialized || '').trim() && !hasAttachments) return null;
  return next;
}

function buildExportSnapshot(snapshot: any, options: any = {}) {
  if (options.includeTools === true) return deepClone(snapshot);
  const next = deepClone(snapshot);
  next.messages = (next.messages || [])
    .map((message: any) => stripToolDataFromMessage(message))
    .filter(Boolean);
  return next;
}

function buildMarkdownExport(snapshot: any) {
  const lines = [
    `# ${snapshot.title || '新会话'}`,
    '',
    `- Agent: ${formatExportValue(snapshot.agent)}`,
    `- Model: ${formatExportValue(snapshot.model)}`,
    `- Mode: ${formatExportValue(snapshot.mode)}`,
    `- CWD: ${formatExportValue(snapshot.cwd)}`,
    `- Updated: ${formatExportValue(snapshot.updated)}`,
    `- Exported: ${new Date().toISOString()}`,
  ];
  if (snapshot.totalCost) lines.push(`- Total Cost: ${snapshot.totalCost}`);
  if (snapshot.totalUsage) lines.push(`- Total Usage: ${JSON.stringify(snapshot.totalUsage)}`);
  lines.push('');

  (snapshot.messages || []).forEach((message: any) => {
    lines.push(`## ${(message.role || 'unknown').toUpperCase()}`);
    if (message.timestamp) lines.push(`_${message.timestamp}_`);
    lines.push('');
    lines.push(serializeMessageContent(message.content || ''));
    if (Array.isArray(message.attachments) && message.attachments.length > 0) {
      lines.push('');
      lines.push('Attachments:');
      message.attachments.forEach((attachment: any) => {
        lines.push(`- ${attachment.filename || attachment.name || attachment.id || 'attachment'}`);
      });
    }
    if (Array.isArray(message.toolCalls) && message.toolCalls.length > 0) {
      message.toolCalls.forEach((toolCall: any) => {
        lines.push('');
        lines.push(`### Tool: ${toolCall.name || 'unknown'}`);
        if (toolCall.input !== undefined) {
          lines.push('```json');
          lines.push(JSON.stringify(toolCall.input, null, 2));
          lines.push('```');
        }
        if (toolCall.result) {
          lines.push('');
          lines.push('```text');
          lines.push(String(toolCall.result));
          lines.push('```');
        }
      });
    }
    lines.push('');
  });

  return lines.join('\n');
}

function downloadTextFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function createSessionExportController(options: SessionExportOptions) {
  function exportCurrentSession(format: 'json' | 'markdown', exportOptions: any = {}) {
    const rawSnapshot = options.getCurrentSnapshot();
    const snapshot = rawSnapshot ? buildExportSnapshot(rawSnapshot, exportOptions) : null;
    if (!snapshot) {
      options.appendError('当前会话尚未完整加载，暂时无法导出。');
      return;
    }
    if (format === 'json') {
      downloadTextFile(
        buildExportFilename(snapshot, 'json'),
        `${JSON.stringify(snapshot, null, 2)}\n`,
        'application/json;charset=utf-8',
      );
      options.showToast('当前会话已导出为 JSON');
      return;
    }
    downloadTextFile(
      buildExportFilename(snapshot, 'md'),
      buildMarkdownExport(snapshot),
      'text/markdown;charset=utf-8',
    );
    options.showToast('当前会话已导出为 Markdown');
  }

  function showSessionExportModal() {
    if (!options.getCurrentSessionId()) {
      options.appendError('请先打开一个会话再导出。');
      return;
    }
    const { overlay, panel, close } = options.createOverlayPanel({
      overlayId: 'export-session-overlay',
      maxWidth: '420px',
      panelHtml: `
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
          <span class="tw-font-mono tw-text-sm tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-slate-900">导出当前会话</span>
          <button id="export-session-close" class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border tw-border-slate-300 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-900" title="关闭">&times;</button>
        </div>
        <div class="tw-mb-4">
          <div class="tw-mb-2 tw-font-mono tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500">导出格式</div>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-border-2 tw-border-slate-900 tw-bg-white tw-px-3 tw-py-2 tw-font-mono tw-text-[13px] tw-font-bold tw-text-slate-900 tw-shadow-[2px_2px_0_0_#0f172a]">
              <input class="tw-m-0 tw-accent-sky-600" type="radio" name="export-format" value="markdown" checked>
              <span>Markdown</span>
            </label>
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-border-2 tw-border-slate-900 tw-bg-white tw-px-3 tw-py-2 tw-font-mono tw-text-[13px] tw-font-bold tw-text-slate-900 tw-shadow-[2px_2px_0_0_#0f172a]">
              <input class="tw-m-0 tw-accent-sky-600" type="radio" name="export-format" value="json">
              <span>JSON</span>
            </label>
          </div>
        </div>
        <div class="tw-mb-5">
          <label class="tw-flex tw-items-center tw-gap-2 tw-border-2 tw-border-slate-900 tw-bg-amber-50 tw-px-3 tw-py-3 tw-font-mono tw-text-[13px] tw-font-bold tw-text-slate-900 tw-shadow-[2px_2px_0_0_#0f172a]">
            <input id="export-include-tools" class="tw-m-0 tw-accent-sky-600" type="checkbox">
            <span>包含工具调用</span>
          </label>
          <div class="tw-mt-2 tw-text-xs tw-leading-5 tw-text-slate-500">默认不导出工具调用和工具结果，便于阅读。需要完整排查时再勾选。</div>
        </div>
        <div class="tw-flex tw-gap-3">
          <button id="export-session-confirm" class="tw-flex-1 tw-border-2 tw-border-slate-900 tw-bg-sky-600 tw-px-4 tw-py-2.5 tw-font-mono tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-shadow-[2px_2px_0_0_#0f172a] hover:tw-translate-x-px hover:tw-translate-y-px hover:tw-shadow-[1px_1px_0_0_#0f172a] active:tw-translate-x-[2px] active:tw-translate-y-[2px] active:tw-shadow-none">导出</button>
        </div>
      `,
    });
    const formatInputs = panel.querySelectorAll<HTMLInputElement>('input[name="export-format"]');
    const includeToolsCheckbox = panel.querySelector('#export-include-tools') as HTMLInputElement | null;
    panel.querySelector('#export-session-close')?.addEventListener('click', close);
    panel.querySelector('#export-session-confirm')?.addEventListener('click', () => {
      const selected = Array.from(formatInputs).find((input) => input.checked)?.value || 'markdown';
      const includeTools = !!includeToolsCheckbox?.checked;
      close();
      exportCurrentSession(selected === 'json' ? 'json' : 'markdown', { includeTools });
    });
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close();
    });
  }

  return {
    showSessionExportModal,
  };
}
